import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CategoryNodeComponent } from '../manage-categories/manage-category-node/manage-category-node.component'
import { AdminCommonHttpService } from '../service/admin-common-http.service';
import { MatDialog } from '@angular/material/dialog';
import { DeleteConfirmDialogComponent } from '../delete-confirm-dialog/delete-confirm-dialog.component';
import { trigger, state, style, animate, transition } from '@angular/animations';

interface Category {
  category_id: number;
  name: string;
  parent_category_id: number | null;
  deleted: boolean;
  children: Category[];
}

@Component({
  selector: 'app-manage-categories',
  standalone: true,
  imports: [CommonModule, FormsModule, CategoryNodeComponent],
  templateUrl: './manage-categories.component.html',
  styleUrl: './manage-categories.component.css',
  animations: [
    trigger('fadeAnimation', [
      state('deleted', style({
        opacity: 0.5,
        backgroundColor: '#f0f0f0',
      })),
      state('active', style({
        opacity: 1,
        backgroundColor: 'white',
      })),
      transition('active <=> deleted', [
        animate('400ms ease-in-out')
      ]),
    ])
  ]
})
export class ManageCategoriesComponent implements OnInit {
  categories: Category[] = [];
  newCategoryName = '';
  parentIdForNewCategory: number | null = null;
  expandedCategories: Set<number> = new Set();

  toggleCategory(categoryId: number): void {
    if (this.expandedCategories.has(categoryId)) {
      this.expandedCategories.delete(categoryId);
    } else {
      this.expandedCategories.add(categoryId);
    }
  }

  constructor(private commonService: AdminCommonHttpService, private dialog: MatDialog) { }

  ngOnInit() {
    localStorage.setItem(`is_admin`, "true");
    this.loadCategories();
  }

  async loadCategories(): Promise<void> {
    const data = await this.commonService.getCategories();
    this.categories = this.buildTree(data);
  }


  buildTree(flatList: Category[]): Category[] {
    const map = new Map<number, Category>();
    const roots: Category[] = [];

    // Ensure children array is initialized
    flatList.forEach(cat => {
      map.set(cat.category_id, { ...cat, children: [] });
    });

    flatList.forEach(cat => {
      if (cat.parent_category_id === null) {
        roots.push(map.get(cat.category_id)!);
      } else {
        const parent = map.get(cat.parent_category_id);
        if (parent) {
          parent.children!.push(map.get(cat.category_id)!);
        }
      }
    });

    return roots;
  }


  startAddTopLevel() {
    this.parentIdForNewCategory = -1;
  }

  startAddSubcategory(parentId: number) {
    this.parentIdForNewCategory = parentId;
  }

  async saveCategory() {
    if (!this.newCategoryName.trim()) return;

    const payload = {
      category_name: this.newCategoryName,
      parent_category_id: this.parentIdForNewCategory === -1 ? null : this.parentIdForNewCategory
    };
    await this.commonService.addCategory(payload);
    this.newCategoryName = "";
    this.parentIdForNewCategory = 0;
  }

  async deleteCategory(categoryId: number): Promise<void> {
    console.log('Deleting category ID', categoryId);

    await this.commonService.deleteCategory(categoryId);

    this.markCategoryDeleted(this.categories, categoryId);
  }

  markCategoryDeleted(categories: any[], categoryId: number): void {
    for (const category of categories) {
      if (category.category_id === categoryId) {
        category.deleted = true;
      }
      if (category.children && category.children.length > 0) {
        this.markCategoryDeleted(category.children, categoryId);
      }
    }
  }



  private deleteCategoryRecursively(categories: any[], categoryId: number): any[] {
    return categories
      .filter(category => category.category_id !== categoryId)
      .map(category => ({
        ...category,
        children: category.children ? this.deleteCategoryRecursively(category.children, categoryId) : []
      }));
  }

  confirmDeleteCategory(category: any): void {
    const hasChildren = category.children && category.children.length > 0;

    let message = '';

    if (hasChildren) {
      message = `This category has subcategories. Deleting it will delete ALL subcategories too. Are you sure you want to continue?`;
    } else {
      message = `Are you sure you want to delete this category?`;
    }

    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { message, buttonValue: 'Delete' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.deleteCategory(category.category_id);
      }
    });
  }

  confirmRestoreCategory(category: any): void {
    const message = `Are you sure you want to restore this category?`;

    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, {
      width: '400px',
      data: { message, buttonValue: 'Restore' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.restoreCategory(category);
      }
    });
  }


  async restoreCategory(category: any): Promise<void> {
    console.log('Restoring category', category.category_id);

    await this.commonService.restoreCategory(category.category_id);

    category.deleted = false;

    // Force Angular to refresh view
    // const flatList = this.flattenTree(this.categories);
    // this.categories = this.buildTree(flatList);
    this.categories = [...this.categories];
  }


  flattenTree(categories: Category[]): Category[] {
    let flatList: Category[] = [];

    for (const category of categories) {
      flatList.push({
        ...category,
        children: [] // clear children temporarily
      });

      if (category.children && category.children.length > 0) {
        flatList = flatList.concat(this.flattenTree(category.children));
      }
    }

    return flatList;
  }







}
